import { PromptModal } from '@/components'
import { useFormWatch } from '@/utils'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { useTimeEntryForm } from '../hooks'
import { exceedsDailyWorkLimit } from '../time-entry-form.schema'
import type { TimeEntryFormValues } from '../types'
import type {
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import { TimeEntryForm } from './time-entry-form'

interface EditTimeEntryModalProps {
  entry: TimesheetsDayReportTimeEntry | null
  salaryWorkTime: TimesheetsSalaryWorkTime
  overtimeCustomChoice: boolean
  existingTimeEntries: TimesheetsDayReportTimeEntry[]
  canSetSalaryCodeManually: boolean
  onResolve: PromptResolve<TimeEntryFormValues | null>
}

export const EditTimeEntryModal: React.FC<EditTimeEntryModalProps> = ({
  entry,
  salaryWorkTime,
  overtimeCustomChoice,
  existingTimeEntries,
  canSetSalaryCodeManually,
  onResolve,
}) => {
  const translations = useTranslate({
    editTimeEntry: 'timesheets.edit-time-entry',
    submit: 'common.submit',
  })

  const form = useTimeEntryForm(entry, salaryWorkTime)

  const [start, end] = useWatch({
    control: form.control,
    name: ['start', 'end'],
  })
  const overtimeSalaryCodeId = useFormWatch(form, 'overtimeSalaryCodeId')

  const overtimeRequired =
    overtimeCustomChoice &&
    exceedsDailyWorkLimit(
      [{ start, end }],
      existingTimeEntries,
      salaryWorkTime.dailyWorkLimit
    )

  const submitDisabled =
    !form.formState.isValid || (overtimeRequired && !overtimeSalaryCodeId)

  return (
    <PromptModal
      className="w-[600px]"
      title={translations.editTimeEntry}
      submitText={translations.submit}
      submitDisabled={submitDisabled}
      cancelOnClickOutside
      onCancel={() => onResolve(null)}
      onSubmit={() => void form.handleSubmit(onResolve)()}
    >
      <TimeEntryForm
        form={form}
        salaryWorkTime={salaryWorkTime}
        canSetSalaryCodeManually={canSetSalaryCodeManually}
        overtimeRequired={overtimeRequired}
        allowPause={false}
      />
    </PromptModal>
  )
}
